import React, {useState, useEffect, useMemo, useCallback} from "react";
import {Divider, Spin, message} from "antd";
import "../briefs.css";
import {NOOP} from "../../utils/NOOP.jsx";
import {generateSquareBriefInfoProvider, generateBriefRowsInfoProvider, initialFormValues} from "./utils.jsx";
import {ProviderBidForm} from "./ProviderBidForm";
import {StyledButton} from "../../components";
import {isEmpty} from "lodash";
import {SquareBriefInfo, BriefInfoRow} from "../utils.jsx";
import {BriefStatus} from "../../company/CompanyAccountProfile/CompanyBriefs/consts.jsx";
import {isProductHasShippingProvider} from "../../admin/upload/utils.js";
import {HttpClient} from "../../http/HttpClient.jsx";
import {removeBriefFromProvider} from "../../api/briefs/briefs.js";
import {useHistory} from "react-router-dom";
import isMobile from "is-mobile";
import {DeclineOrderModal} from "../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {
    briefProviderDeclineOptionsToText,
    PROVIDER_BRIEF_DECLINE_MODAL_HEADER,
    PROVIDER_BRIEF_DECLINE_MODAL_SUBTITLE
} from "../../event/DeclineOrderModal/consts.js";

export const ProviderBriefDetailsView = ({
    onSendBid = NOOP,
    briefInfo,
    bidInfo,
    loadingBrief = false,
    providerId,
    bidProduct
}) => {
    const [brief, setBrief] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formValues, setFormValues] = useState(initialFormValues);
    const [loadingDeliveryPrice, setLoadingDeliveryPrice] = useState(false);
    const [declineBriefModalVisible, setDeclineBriefModalVisible] = useState(false);
    const history = useHistory();
    const orderSquareInfo = useMemo(() => generateSquareBriefInfoProvider(brief), [brief]);
    const orderRowsInfo = useMemo(() => generateBriefRowsInfoProvider(brief), [brief]);

    useEffect(() => {
        if (!isEmpty(briefInfo)) {
            setBrief(briefInfo);
            setFormValues(prev => ({...prev, dtstart: briefInfo?.dtstart}));
        }
    }, [briefInfo]);

    useEffect(() => {
        if (bidInfo && !isEmpty(bidInfo)) {
            setFormValues(prev => ({...prev, ...bidInfo}));
            setIsFormOpen(true);
        }
    }, [bidInfo]);

    useEffect(() => {
        if (isFormOpen && !bidInfo?.deliveryPrice) {
            calculateDeliveryPrice();
        }
    }, [isFormOpen]);

    const onDeclineBrief = useCallback(
        async declineDetails => {
            const res = await removeBriefFromProvider(
                brief.briefId,
                providerId,
                declineDetails.declineReason,
                declineDetails.declineComment
            );
            if (res?.error) {
                return;
            }
            setDeclineBriefModalVisible(false);
            return history.push(`/dashboard/briefs`);
        },
        [history, brief?.briefId, providerId]
    );

    const calculateDeliveryPrice = useCallback(async () => {
        if (isProductHasShippingProvider(bidProduct) && !isEmpty(bidProduct)) {
            setLoadingDeliveryPrice(true);
            const deliveryPriceRes = await HttpClient.safePost(`/api/products/shippingPrice?userId=${brief?.userId}`, [
                {
                    productId: bidProduct.productId,
                    destinationPlaceId: brief?.placeId
                }
            ]);

            if (!deliveryPriceRes?.error) {
                const [firstDeliveryPriceRes] = deliveryPriceRes;

                setFormValues(prev => ({
                    ...prev,
                    deliveryPrice: firstDeliveryPriceRes.price,
                    originalDeliveryPrice: firstDeliveryPriceRes.originalDeliveryPrice,
                    siteId: firstDeliveryPriceRes.siteId
                }));
            }
            setLoadingDeliveryPrice(false);
        }
    }, [bidProduct, brief?.userId, brief?.placeId]);

    const onSaveBid = useCallback(
        async (values, isBidByFile) => {
            await onSendBid({
                ...formValues,
                briefId: brief?.briefId,
                address: brief?.address,
                participantsAmount: Number(formValues.participantsAmount),
                ...(formValues.deliveryPrice && {deliveryPrice: Number(formValues.deliveryPrice)}),
                providerId: bidInfo?.providerId || providerId,
                systemId: brief?.systemId,
                package: {
                    ...formValues?.package,
                    ...(!isBidByFile
                        ? {
                              options: formValues?.package?.options?.map(({key, amount, price, ...rest}) => ({
                                  ...rest,
                                  amount: Number(amount),
                                  price: Number(price)
                              }))
                          }
                        : {options: []})
                }
            });
        },
        [onSendBid, formValues, brief?.briefId, brief?.address, brief?.systemId, bidInfo?.providerId, providerId]
    );

    if (loadingBrief) {
        return (
            <div className="provider-brief-details-card" style={{textAlign: "center", padding: 20}}>
                <Spin size="large" />
            </div>
        );
    }

    if (!brief) {
        return (
            <div className="provider-brief-details-card" style={{padding: 20}}>
                <p>פרטי הבריף לא נמצאו.</p>
            </div>
        );
    }

    return (
        <div className="provider-brief-details-card">
            {bidInfo?.note && (
                <div className="bid-note-container">
                    <div className="bid-note-title">התיקונים שביקש הלקוח</div>
                    <div className="bid-note-content">{bidInfo?.note}</div>
                </div>
            )}

            <div className="provider-brief-header">
                <div className="provider-brief-title">
                    <span className="brief-title">בריף הלקוח</span>
                    <span>אלה הפרטים שהלקוח ביקש לבנות לפיהם הצעת מחיר</span>
                </div>
                <div className="provider-brief-summary">
                    {orderSquareInfo?.map(({row = []}, index) => {
                        return <SquareBriefInfo key={index} row={row} />;
                    })}
                    {orderRowsInfo?.map(({title, component}, index) => {
                        return <BriefInfoRow key={index} title={title} component={component} />;
                    })}
                </div>
            </div>

            <Divider style={{margin: "12px 0"}} />

            {!loadingBrief && !isFormOpen && briefInfo?.status !== BriefStatus.cancelled && (
                <div className="bid-actions">
                    <StyledButton className="add-item-btn" onClick={() => setIsFormOpen(true)}>
                        מעוניין להגיש הצעת מחיר
                    </StyledButton>
                    <StyledButton
                        className="add-item-btn cancel-bid-btn"
                        onClick={() => setDeclineBriefModalVisible(true)}>
                        מסיר השתתפות בהצעת מחיר
                    </StyledButton>
                </div>
            )}

            {briefInfo?.status === BriefStatus.cancelled && (
                <div className="bid-note-container">
                    <div className="bid-note-title">הבריף נסגר להצעות מחיר חדשות</div>
                    <div className="bid-note-content">
                        תודה על הרצון והזמינות שלך, נשמח לשיתופי פעולה נוספים בהזדמנויות הבאות.
                    </div>
                </div>
            )}

            {isFormOpen && (
                <ProviderBidForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                    brief={brief}
                    onSubmit={onSaveBid}
                    providerId={providerId}
                    bidHaveFiles={bidInfo?.package?.files?.length > 0}
                    bidHaveOptions={bidInfo?.package?.options?.length > 0}
                    bidProduct={bidProduct}
                    loadingDeliveryPrice={loadingDeliveryPrice}
                />
            )}
            <DeclineOrderModal
                visible={declineBriefModalVisible}
                onSave={onDeclineBrief}
                onClose={() => setDeclineBriefModalVisible(false)}
                header={PROVIDER_BRIEF_DECLINE_MODAL_HEADER}
                subTitle={PROVIDER_BRIEF_DECLINE_MODAL_SUBTITLE}
                declineReasons={briefProviderDeclineOptionsToText}
                showWhoToNotify={false}
                declineReasonLabel="סיבת הסרת ההשתתפות"
                declineCommentLabel="ניתן להוסיף כאן תיאור של סיבת הסרת מועמדות"
            />
        </div>
    );
};
